/**
 * Search helpers for fuzzy matching with stemming support
 */


const SUFFIX_RULES = [
  { suffix: 'ies', replacement: 'y', minStem: 2 },
  { suffix: 'ves', replacement: 'f', minStem: 2 },
  { suffix: 'oes', replacement: 'o', minStem: 2 },
  { suffix: 'ses', replacement: 's', minStem: 2 },
  { suffix: 'xes', replacement: 'x', minStem: 2 },
  { suffix: 'ches', replacement: 'ch', minStem: 2 },
  { suffix: 'shes', replacement: 'sh', minStem: 2 },
  { suffix: 'sses', replacement: 'ss', minStem: 2 },
  { suffix: 'es', replacement: '', minStem: 3 },
  { suffix: 's', replacement: '', minStem: 3 },
  
  { suffix: 'ing', replacement: '', minStem: 3 },
  { suffix: 'ed', replacement: '', minStem: 3 },
  { suffix: 'er', replacement: '', minStem: 3 },
  { suffix: 'est', replacement: '', minStem: 3 },
  { suffix: 'ly', replacement: '', minStem: 3 },
  { suffix: 'ment', replacement: '', minStem: 3 },
  { suffix: 'ness', replacement: '', minStem: 3 },
  { suffix: 'tion', replacement: '', minStem: 3 },
  { suffix: 'able', replacement: '', minStem: 3 },
  { suffix: 'ible', replacement: '', minStem: 3 },
];

const DOUBLE_CONSONANTS = ['bb', 'dd', 'ff', 'gg', 'mm', 'nn', 'pp', 'rr', 'ss', 'tt', 'll'];

/**
 * @param {string} word
 * @returns {string}
 */
export const stem = (word) => {
  if (!word || word.length < 3) return word;
  
  let stemmed = word.toLowerCase();
  
  // Apply suffix rules
  for (const rule of SUFFIX_RULES) {
    if (stemmed.endsWith(rule.suffix) && stemmed.length - rule.suffix.length >= rule.minStem) {
      stemmed = stemmed.slice(0, -rule.suffix.length) + rule.replacement;
      break;
    }
  }
  
  for (const dc of DOUBLE_CONSONANTS) {
    if (stemmed.endsWith(dc)) {
      stemmed = stemmed.slice(0, -1);
      break;
    }
  }
  
  return stemmed;
};

/**
 * Get all variations of a word for matching (original + stemmed)
 * @param {string} word
 * @returns {string[]} Array of word variations
 */
export const getWordVariations = (word) => {
  if (!word) return [];
  
  const lower = word.toLowerCase();
  const stemmed = stem(lower);
  
  const variations = new Set([lower, stemmed]);
  
  if (!lower.endsWith('s')) {
    variations.add(lower + 's');
    if (lower.endsWith('y')) {
      variations.add(lower.slice(0, -1) + 'ies');
    }
    if (lower.endsWith('f')) {
      variations.add(lower.slice(0, -1) + 'ves');
    }
  }
  
  return Array.from(variations);
};

/**
 * Check if two words match
 * @param {string} queryWord - The search query word
 * @param {string} targetWord - The word to match against
 * @returns {number} Match score (0 = no match, higher = better match)
 */
export const fuzzyWordMatch = (queryWord, targetWord) => {
  if (!queryWord || !targetWord) return 0;
  
  const qLower = queryWord.toLowerCase();
  const tLower = targetWord.toLowerCase();
  
  // Exact match
  if (qLower === tLower) return 1;
  
  // Stem comparison
  const qStem = stem(qLower);
  const tStem = stem(tLower);
  
  if (qStem === tStem) return 0.95;

  const MIN_FRAGMENT = 4;

  if (tStem.startsWith(qStem) && qStem.length >= 2) return 0.85;
  if (tLower.startsWith(qLower) && qLower.length >= 2) return 0.8;

  if (qStem.startsWith(tStem) && tStem.length >= MIN_FRAGMENT) return 0.85;
  if (qLower.startsWith(tLower) && tLower.length >= MIN_FRAGMENT) return 0.8;

  if (tLower.includes(qLower) && qLower.length >= 2) return 0.6;
  if (qLower.includes(tLower) && tLower.length >= MIN_FRAGMENT) return 0.6;
  if (tStem.includes(qStem) && qStem.length >= 2) return 0.5;
  if (qStem.includes(tStem) && tStem.length >= MIN_FRAGMENT) return 0.5;

  return 0;
};

/**
 * Calculate match score between query tokens and searchable text
 * @param {string[]} queryTokens - Tokenized query
 * @param {string} searchableText - Text to search in
 * @param {string[]} searchableTokens - Pre-tokenized searchable text
 * @returns {number} Match score (0-1)
 */
export const calculateTokenMatchScore = (queryTokens, searchableText, searchableTokens) => {
  if (!queryTokens?.length) return 0;
  
  const searchLower = searchableText.toLowerCase();
  let totalScore = 0;
  
  for (const queryToken of queryTokens) {
    let bestMatch = 0;
    
    // Check against each searchable token
    for (const searchToken of searchableTokens) {
      const matchScore = fuzzyWordMatch(queryToken, searchToken);
      bestMatch = Math.max(bestMatch, matchScore);
      
      if (matchScore === 1) break;
    }
    
    if (bestMatch < 0.6 && searchLower.includes(queryToken.toLowerCase())) {
      bestMatch = Math.max(bestMatch, 0.5);
    }
    
    totalScore += bestMatch;
  }
  
  return totalScore / queryTokens.length;
};

// Stop words to filter out from tokenization
export const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been',
  'i', 'me', 'my', 'we', 'our', 'you', 'your', 'it', 'its', 'near', 'nearby',
  'find', 'get', 'help', 'need', 'want', 'looking'
]);

/**
 * Tokenize text for search
 * @param {string} text - Text to tokenize
 * @param {Object} options - Tokenization options
 * @param {boolean} options.allowSingleChar - Allow single character tokens (for short queries)
 * @param {boolean} options.filterStopWords - Filter out stop words
 * @returns {string[]} Array of tokens
 */
export const tokenize = (text, options = {}) => {
  const { allowSingleChar = false, filterStopWords = true } = options;
  
  if (!text) return [];
  
  const minLength = allowSingleChar ? 1 : 2;
  
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => {
      if (word.length < minLength) return false;
      if (filterStopWords && STOP_WORDS.has(word)) return false;
      return true;
    });
};

const NL_QUESTION_WORDS = new Set([
  'what', 'whats', "what's",
  'how', 'hows', "how's",
  'why',
  'where', 'wheres', "where's",
  'when', 'whens', "when's",
  'who', 'whos', "who's",
  'which',
  'can', 'cant', "can't",
  'is', 'isnt', "isn't",
  'are', 'arent', "aren't",
  'do', 'dont', "don't",
  'does', 'doesnt', "doesn't",
  'should', 'shouldnt', "shouldn't",
  'could', 'couldnt', "couldn't",
  'would', 'wouldnt', "wouldn't",
  'will', 'wont', "won't",
  'am',
]);

const NL_PERSONAL_WORDS = new Set([
  'my', 'i', "i'm", 'im', "i've", 'ive', "i'd", 'id',
  'we', "we're", 'were', "we've", 'weve',
]);

/**
 * @param {string} text
 * @returns {boolean}
 */
export const isNaturalLanguageQuery = (text) => {
  if (!text) return false;

  const trimmed = String(text).trim();
  if (!trimmed) return false;

  if (trimmed.endsWith('?')) return true;

  const rawWords = trimmed.toLowerCase().split(/\s+/).filter(Boolean);
  if (rawWords.length === 0) return false;

  const firstWord = rawWords[0].replace(/[^\w']/g, '');

  if (NL_QUESTION_WORDS.has(firstWord)) return true;
  if (NL_PERSONAL_WORDS.has(firstWord)) return true;

  const lower = trimmed.toLowerCase();
  const hasLocationPhrase =
    lower.includes('near me') || lower.includes('nearby');
  if (hasLocationPhrase && rawWords.length >= 4) return true;

  if (rawWords.length >= 4) return true;

  return false;
};

/**
 * Get popular/default categories for fallback suggestions
 * @param {Array} categories - All flattened categories
 * @param {number} limit - Max number to return
 * @returns {Array} Popular categories
 */
export const getPopularCategories = (categories, limit = 5) => {
  const popularKeys = [
    'veterinarian',
    'pet-stores',
    'animal-shelters',
    'emergency-veterinarian',
    'dog-parks',
    'grooming',
    'pet-boarding',
    'pet-adoption'
  ];
  
  const popular = [];
  
  for (const key of popularKeys) {
    const found = categories.find(c => c.key === key);
    if (found && popular.length < limit) {
      popular.push(found);
    }
  }
  
  // Fill remaining with main categories (no parentKey)
  if (popular.length < limit) {
    const mainCats = categories.filter(c => !c.parentKey && !popular.some(p => p.key === c.key));
    popular.push(...mainCats.slice(0, limit - popular.length));
  }
  
  return popular;
};

